import viewService from "@/services/viewService";
import EntityService from "@/services/entityService";
import relatorioContasAReceberService from "@/views/Relatorios/services/relatorioContasAReceberService";
import relatorioNFEmitidaService from "@/views/Relatorios/services/relatorioNFEmitidaService";

const projectService = new EntityService("Project");

const JOB_VIEW_ID = "d6b789aa-34e6-4403-a5b8-7ee19c20efb8";
const CONTAS_A_RECEBER_VIEW_NAME = "contas-a-receber";

const toNumber = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const normalizarTexto = (valor) => String(valor || "").trim();
const normalizarCodigoJob = (valor) => normalizarTexto(valor).replace(/\s+/g, "").toUpperCase();

const extrairTexto = (valor) => {
  if (Array.isArray(valor)) {
    if (!valor.length) return "";
    return extrairTexto(valor[0]);
  }

  if (valor && typeof valor === "object") {
    return normalizarTexto(valor.name || valor.nome || valor.fullName || valor.displayName || valor.code || valor.codigo);
  }

  return normalizarTexto(valor);
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const possuiDadosValidos = (response) => {
  if (!response) return false;
  if (response?.success === false) return false;
  if (response?.data?.success === false) return false;
  return Array.isArray(response?.data?.data?.data) || Array.isArray(response?.data?.data) || Array.isArray(response?.data);
};

const obterCoordenadorContrato = (linha = {}, revisao = {}) => {
  const candidatos = [
    linha?.coordenadorDeContrato,
    linha?.coordenadorContrato,
    linha?.coordenadorContratoNome,
    linha?.coordenador,
    linha?.coordenadorNome,
    linha?.contractCoordinator,
    linha?.contractCoordinatorName,
    revisao?.coordenadorDeContrato,
    revisao?.coordenadorContrato,
    revisao?.coordenadorContratoNome,
    revisao?.coordenador,
    revisao?.coordenadorNome,
    revisao?.contractCoordinator,
    revisao?.contractCoordinatorName,
    linha?.vendedorCoordenador,
    linha?.vendedorNome,
    revisao?.vendedorCoordenador,
    revisao?.vendedorNome
  ];

  for (const candidato of candidatos) {
    const texto = extrairTexto(candidato);
    if (texto) return texto;
  }

  return "-";
};

const normalizarItensJob = (linha = {}, revisao = {}) => {
  const candidatos = [
    revisao?.jobItens,
    linha?.jobItens,
    revisao?.itens,
    linha?.itens
  ];

  const lista = candidatos.find((valor) => Array.isArray(valor)) || [];

  return lista.map((item, index) => ({
    _id: item?._id || item?.id || `${linha?.code || "job"}-item-${index}`,
    codigo: item?.codigo || item?.sequencia || item?.itemCodigo || "-",
    descricao: normalizarTexto(item?.descricao || item?.description || item?.itemDescricao || "-"),
    quantidade: toNumber(item?.quantidade ?? item?.qtd ?? item?.quantity ?? 0),
    valorTotal: toNumber(item?.precoTotal ?? item?.valorTotal ?? item?.total ?? 0)
  }));
};

const normalizarLinha = (linha = {}) => {
  const revisao = Array.isArray(linha?.revisoes) ? (linha.revisoes[0] || {}) : (linha?.revisoes || {});
  const itensJob = normalizarItensJob(linha, revisao);

  return {
    _id: linha?._id || linha?.id || `${linha?.code || "job"}-${Math.random()}`,
    code: linha?.code || "-",
    pedidoCompra: revisao?.pedidoCompra || linha?.pedidoCompra || "-",
    dataAbertura: revisao?.dataAbertura || linha?.dataAbertura || null,
    dataEncerramento: revisao?.dataEncerramento || linha?.dataEncerramento || null,
    clienteNome: linha?.clienteNome || revisao?.clienteNome || "-",
    description: revisao?.description || linha?.description || "-",
    vendedorCoordenador: obterCoordenadorContrato(linha, revisao),
    valorJob: toNumber(linha?.valorJob ?? revisao?.valorJob ?? 0),
    valorRecebido: 0,
    saldoAReceber: 0,
    valorFaturado: 0,
    saldoEmCarteira: 0,
    segmento: linha?.segmentoMercado || linha?.segmento || revisao?.segmentoMercado || revisao?.segmento || "",
    itensJob,
    itensQuantidade: itensJob.length
  };
};

const listarPorView = async (params = []) => {
  try {
    const response = await viewService.viewRunById(JOB_VIEW_ID, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  } catch (_) {
    // fallback para entidade
  }

  return [];
};

const listarPorEntidade = async () => {
  const response = await projectService.getAll(1, 5000, "", "", null, null, null, "code");
  return extrairLista(response);
};

const listarContasAReceberDaTela = async (filtros = {}) => {
  const params = [];

  if (filtros?.dataInicio) {
    params.push({ dataType: "String", name: "dataInicio", value: filtros.dataInicio });
  }

  if (filtros?.dataFim) {
    params.push({ dataType: "String", name: "dataFim", value: filtros.dataFim });
  }

  try {
    const response = await viewService.viewRun(CONTAS_A_RECEBER_VIEW_NAME, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  } catch (_) {
    // fallback no serviço de relatório
  }

  return [];
};

const filtrarLista = (lista = [], filtros = {}) => {
  const inicio = filtros?.dataInicio ? new Date(filtros.dataInicio) : null;
  const fim = filtros?.dataFim ? new Date(filtros.dataFim) : null;
  const jobFiltro = normalizarTexto(filtros?.job).toLowerCase();
  const clienteFiltro = normalizarTexto(filtros?.cliente).toLowerCase();
  const segmentoFiltro = normalizarTexto(filtros?.segmento).toLowerCase();

  return lista.filter((item) => {
    const dataLinha = item?.dataAbertura ? new Date(item.dataAbertura) : null;
    const dataInicioOk = !inicio || !dataLinha || Number.isNaN(dataLinha.getTime()) || dataLinha >= inicio;
    const dataFimOk = !fim || !dataLinha || Number.isNaN(dataLinha.getTime()) || dataLinha <= fim;

    const codeOk = !jobFiltro || normalizarTexto(item?.code).toLowerCase().includes(jobFiltro);
    const clienteOk = !clienteFiltro || normalizarTexto(item?.clienteNome).toLowerCase().includes(clienteFiltro);
    const segmentoOk = !segmentoFiltro || normalizarTexto(item?.segmento).toLowerCase().includes(segmentoFiltro);

    return dataInicioOk && dataFimOk && codeOk && clienteOk && segmentoOk;
  });
};

const somarRecebimentosPorJob = (linhas = []) => {
  return (Array.isArray(linhas) ? linhas : []).reduce((acc, linha) => {
    const chave = normalizarCodigoJob(
      linha?.job?.codigo ||
      linha?.jobCodigo ||
      linha?.jobCode ||
      linha?.codigoJob ||
      linha?.job ||
      linha?.project?.code ||
      linha?.project
    );

    if (!chave) return acc;

    const valorRecebido = toNumber(
      linha?.valorRecebido ??
      linha?.recebido ??
      linha?.valorLiquidado ??
      linha?.valorRecebimentos ??
      (toNumber(linha?.valorLiquido) - toNumber(linha?.saldo))
    );

    acc[chave] = toNumber(acc[chave]) + Math.max(0, valorRecebido);
    return acc;
  }, {});
};

const somarFaturamentoPorJob = (linhas = []) => {
  return (Array.isArray(linhas) ? linhas : []).reduce((acc, linha) => {
    const chave = normalizarCodigoJob(linha?.job || linha?.codigoJob || linha?.jobCode);
    if (!chave) return acc;

    acc[chave] = toNumber(acc[chave]) + toNumber(linha?.valorTotal);
    return acc;
  }, {});
};

export default {
  async listarRelatorioJob(filtros = {}) {
    const params = [
      { dataType: "String", name: "dataInicio", value: filtros?.dataInicio || "1970-01-01" },
      { dataType: "String", name: "dataFim", value: filtros?.dataFim || new Date().toISOString().slice(0, 10) },
      { dataType: "String", name: "codigo", value: filtros?.job || "" },
      { dataType: "String", name: "cliente", value: normalizarTexto(filtros?.cliente).toUpperCase() },
      { dataType: "String", name: "segmento", value: filtros?.segmento || "" }
    ];

    let lista = await listarPorView(params);

    if (!Array.isArray(lista) || !lista.length) {
      lista = await listarPorEntidade();
    }

    const normalizada = (Array.isArray(lista) ? lista : []).map((item) => normalizarLinha(item));
    const filtrada = filtrarLista(normalizada, filtros);

    const [contasAReceberTela, contasAReceberRelatorio, nfEmitida] = await Promise.all([
      listarContasAReceberDaTela(filtros),
      relatorioContasAReceberService.listarContasAReceber({
        dataInicio: filtros?.dataInicio || "",
        dataFim: filtros?.dataFim || ""
      }),
      relatorioNFEmitidaService.listarNFEmitida({
        dataInicio: filtros?.dataInicio || "",
        dataFim: filtros?.dataFim || ""
      })
    ]);

    const baseContasAReceber = Array.isArray(contasAReceberTela) && contasAReceberTela.length
      ? contasAReceberTela
      : contasAReceberRelatorio;

    const recebimentosPorJob = somarRecebimentosPorJob(baseContasAReceber);
    const faturadoPorJob = somarFaturamentoPorJob(nfEmitida);

    return filtrada.map((item) => {
      const chaveJob = normalizarCodigoJob(item?.code);
      const valorRecebido = toNumber(recebimentosPorJob[chaveJob] || 0);
      const valorFaturado = toNumber(faturadoPorJob[chaveJob] || 0);
      const saldoAReceber = toNumber(item.valorJob) - valorRecebido;
      const saldoEmCarteira = Math.max(0, valorRecebido - valorFaturado);

      return {
        ...item,
        valorRecebido,
        saldoAReceber,
        valorFaturado,
        saldoEmCarteira
      };
    });
  }
};
